import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { RouteData } from './shared/models/auth';
import { AppRoles } from './shared/models/appRoles';
import { ChooseMandatComponent } from './modules/auth/choose-mandat/choose-mandat.component';
import { SalesAdminDashboardComponent } from './modules/dashboard/dashboard/sales-admin-dashboard/sales-admin-dashboard.component';
import { SalesAdminLightDashboardComponent } from './modules/dashboard/dashboard/sales-admin-light-dashboard/sales-admin-light-dashboard.component';
import { SalesDashboardComponent } from './modules/dashboard/dashboard/sales-dashboard/sales-dashboard.component';
import { MandatDashboardComponent } from './modules/dashboard/dashboard/mandat-dashboard/mandat-dashboard.component';
import { AccountingDashboardComponent } from './modules/dashboard/dashboard/accounting-dashboard/accounting-dashboard.component';
import { PurchaseDashboardComponent } from './modules/dashboard/dashboard/purchase-dashboard/purchase-dashboard.component';
import { inject } from '@angular/core';
import { AnalyticsService } from './modules/dashboard/analytics.service';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: '', pathMatch: 'full', redirectTo: 'sign-in' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'example'
    },

    {
        path: 'choose-mandat',
        component: ChooseMandatComponent,

    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
        ]
    },


    // Auth routes for guests
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
        ]
    },

    // Admin routes
    {
        path: '',
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [

            //Dashboards
            {
                canActivate: [AuthGuard],
                path: 'dashboard/sales-admin', component: SalesAdminDashboardComponent,
                data: { roles: [AppRoles.SalesAdmin] } as RouteData,
            },
            {
                canActivate: [AuthGuard],
                path: 'dashboard/sales-admin-light', component: SalesAdminLightDashboardComponent,
                data: { roles: [AppRoles.SaleAdminLight] } as RouteData,
            },
            {
                canActivate: [AuthGuard],
                path: 'dashboard/sales', component: SalesDashboardComponent
            },
            {
                canActivate: [AuthGuard],
                path: 'dashboard/mandat-admin', component: MandatDashboardComponent,
                resolve: {
                    data: () => inject(AnalyticsService).getData(),
                },
            },
            {
                canActivate: [AuthGuard],
                path: 'dashboard/accounting', component: AccountingDashboardComponent,
                resolve: {
                    data: () => inject(AnalyticsService).getData(),
                },
            },
            {
                canActivate: [AuthGuard],
                path: 'dashboard/purchase', component: PurchaseDashboardComponent, resolve: {
                    data: () => inject(AnalyticsService).getData(),
                },
            },

            // Auth routes for legacy carPro users redirected from the legacy CarPro solution
            {
                path: 'redirected-from-legacy/dashboard',
                data: {
                    layout: 'empty'
                } as RouteData,
                resolve: {
                    data: () => inject(AnalyticsService).getData(),
                },
                canActivate: [AuthGuard],
                loadComponent: () => import('./modules/dashboard/dashboard.component').then(mod => mod.DashboardComponent)
            },

            //Other modules
            {
                path: 'statistics/contracts',
                loadChildren: () => import('./modules/statistics/contracts-statistics/contracts-statistics.routes'),
            },
            {
                path: 'vehicles',
                loadChildren: () => import('./modules/vehicles/vehicle.routes').then(mod => mod.routes),
            },
            {
                path: 'sales/delivery-notes',
                loadChildren: () => import('./modules/delivery-notes/delivery-notes.routes').then(mod => mod.routes),
            },
            {
                path: 'sales/warranties',
                loadChildren: () => import('./modules/vehicle-warranties/warranties.routes').then(mod => mod.routes),
            },
            {
                path: 'accounting/cash-book',
                loadChildren: () => import('./modules/accounting/cash-book/cash-book.routes').then(mod => mod.routes),
            },
            {
                path: 'time-tracking',
                canActivateChild: [AuthGuard],
                loadChildren: () => import('./modules/time-tracking/time-tracking.routes').then(mod => mod.routes),
            },
            {
                path: 'tickets',
                loadChildren: () => import('./modules/ticketing/ticketing.routes').then(mod => mod.routes),
            },
            {
                path: 'operator/tickets',
                loadChildren: () => import('./modules/ticketing/operator-ticketing.routes').then(mod => mod.routes),
            },
            {
                path: 'clients',
                loadChildren: () => import('./modules/clients/client.routes').then(mod => mod.routes)
            },
            {
                path: 'partners',
                loadChildren: () => import('./modules/partners/partner.routes').then(mod => mod.routes),
            },
            {
                path: 'guidelines',
                loadChildren: () => import('./modules/guidelines/guidelines.routes').then(m => m.guidelinesRoutes),
            },
            {
                path: 'settings',
                canActivate: [AuthGuard],
                children: [
                    {
                        path: '',
                        data: {
                            layout: '', // or whatever layout you want
                            roles: [AppRoles.MandatAdmin],
                            breadcrumbs: [{ label: 'Settings', url: '/settings' }]
                        } as RouteData,
                        loadComponent: () =>
                            import('./modules/mandat-setting/mandat-setting-details.component')
                                .then(m => m.MandatSettingDetailsComponent),
                    },
                    {
                        path: 'legacy',
                        data: {
                            layout: 'empty',
                            roles: [AppRoles.MandatAdmin],
                            breadcrumbs: [{ label: 'Settings', url: '/settings/legacy' }],
                        } as RouteData,
                        loadComponent: () =>
                            import('./modules/mandat-setting/mandat-setting-details.component')
                                .then(m => m.MandatSettingDetailsComponent),
                    },
                ]
            },
            {
                path: 'account/tenants',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/tenants/tenant.routes').then(mod => mod.routes),
            },
            {
                path: 'account/mandats',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/mandats/mandat.routes').then(mod => mod.routes),
            },
            {
                path: 'account/users',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/admin-users/admin-users.routes').then(mod => mod.routes),
            },
            {
                path: 'general-data/countries',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/general-data/countries/countries.routes').then(mod => mod.routes),
            },
            {
                path: 'general-data/cantons',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/general-data/cantons/cantons.routes').then(mod => mod.routes),
            },
            {
                path: 'general-data/banks',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/general-data/banks/banks.routes').then(mod => mod.routes),
            },
            {
                path: 'general-data/roles',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/general-data/roles/general-data-roles.routes').then(mod => mod.routes),
            },
            {
                path: 'general-data',
                loadChildren: () => import('./modules/organization/general-data/reference-list/general-data-reference.routes').then(mod => mod.routes),
            },
            {
                path: 'customs-department/co2-cost',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/customs-department/co2-costs/co2-costs.routes').then(mod => mod.routes),
            },
            {
                path: 'customs-department/median-deadweight',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/customs-department/median-deadweights/median-deadweights.routes').then(mod => mod.routes),
            },
            {
                path: 'customs-department/config',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/customs-department/customs-configs/customs-configs.routes').then(mod => mod.routes),
            },
            {
                path: 'admin/vin-request-history',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/admin-vin-request-history/admin-vin-request-history.routes').then(mod => mod.routes),
            },
            // Deferred: keep this page for a later decision on whether it is needed.
            /* {
                path: 'account/system-admins',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/system-admins/systemadmin.routes').then(mod => mod.routes),
            }, */
            {
                path: 'account/registration-users',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/organization/registration-users/registration-users.routes').then(mod => mod.routes),
            },
            {
                path: 'administration/vin-request-history',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.SystemAdmin, AppRoles.MandatAdmin, AppRoles.SalesAdmin, AppRoles.Purchase] } as RouteData,
                loadChildren: () => import('./modules/administration/vin-request-history/vin-request-history.routes').then(mod => mod.routes),
            },
            {
                path: 'energy-labels',
                canActivate: [AuthGuard],
                data: { roles: [AppRoles.Admin] } as RouteData,
                loadChildren: () => import('./modules/energy-labels/energy-labels.routes').then(mod => mod.routes),
            },
        ]
    },
    // 404 & Catch all
    { path: '404-not-found', pathMatch: 'full', loadChildren: () => import('app/modules/error/error-404/error-404.routes') },
    { path: '**', redirectTo: '404-not-found' }
];
